import { cn } from '@playbooks/ui/lib/utils';

import { type VariantProps, cva } from 'class-variance-authority';

const avatarMediaVariants = cva('shrink-0 rounded-full', {
	variants: {
		size: {
			xs: 'size-6',
			sm: 'size-8',
			md: 'size-10',
			lg: 'size-12',
		},
	},
	defaultVariants: {
		size: 'sm',
	},
});

type AvatarSize = NonNullable<VariantProps<typeof avatarMediaVariants>['size']>;

const Avatar = ({ className, ...props }: React.ComponentProps<'div'>) => {
	// Theme
	const rootClassName = cn('relative flex shrink-0 items-center gap-2', className);

	// Render
	return <div className={rootClassName} {...props} />;
};

const AvatarImage = ({
	size = 'sm',
	className,
	alt = '',
	...props
}: React.ComponentProps<'img'> & VariantProps<typeof avatarMediaVariants>) => {
	// Theme
	const rootClassName = cn(avatarMediaVariants({ size }), 'object-cover', className);

	// Render
	return <img className={rootClassName} alt={alt} {...props} />;
};

const AvatarFallback = ({
	size = 'sm',
	className,
	...props
}: React.ComponentProps<'div'> & VariantProps<typeof avatarMediaVariants>) => {
	// Theme
	const rootClassName = cn(
		avatarMediaVariants({ size }),
		'flex items-center justify-center bg-surface-hover text-xs text-foreground-body',
		className,
	);

	// Render
	return <div className={rootClassName} {...props} />;
};

const AvatarContent = ({ className, ...props }: React.ComponentProps<'div'>) => {
	// Theme
	const rootClassName = cn('min-w-0 flex-1', className);

	// Render
	return <div className={rootClassName} {...props} />;
};

const AvatarTitle = ({ className, ...props }: React.ComponentProps<'div'>) => {
	// Theme
	const rootClassName = cn('truncate text-sm font-medium', className);

	// Render
	return <div className={rootClassName} {...props} />;
};

const AvatarDescription = ({ className, ...props }: React.ComponentProps<'p'>) => {
	// Theme
	const rootClassName = cn('truncate text-xs text-foreground-body', className);

	// Render
	return <p className={rootClassName} {...props} />;
};

const AvatarAction = ({ className, ...props }: React.ComponentProps<'div'>) => {
	// Theme
	const rootClassName = cn('ml-auto flex items-center gap-2', className);

	// Render
	return <div className={rootClassName} {...props} />;
};

const AvatarBadge = ({ className, ...props }: React.ComponentProps<'span'>) => {
	// Theme
	const rootClassName = cn('absolute right-0 bottom-0 size-2 rounded-full ring-2 ring-canvas', className);

	// Render
	return <span className={rootClassName} {...props} />;
};

export {
	Avatar,
	AvatarAction,
	AvatarBadge,
	AvatarContent,
	AvatarDescription,
	AvatarFallback,
	AvatarImage,
	AvatarTitle,
	avatarMediaVariants,
};
export type { AvatarSize };
